import {
  clampMark,
  clampSlot,
  newMarkId,
  newSlotId,
} from "./skillAgentMoodboardLayout";

const NUDGE = 0.028;

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

/** Fotos con asset, textos y/o floats del pasteboard. */
export function buildMoodboardClipboard({
  slots = [],
  marks = [],
  selectedSlotIds = [],
  selectedMarkIds = [],
  selectedMarkId = null,
  pasteboard = [],
  selectedPasteboardIds = [],
  fromPage = 0,
} = {}) {
  const ids = new Set((selectedSlotIds || []).map((id) => String(id)));
  const photos = (slots || [])
    .filter((s) => ids.has(String(s.id)) && s?.assetId != null)
    .map((s) => clone(s));
  const markIds = new Set(
    (Array.isArray(selectedMarkIds) && selectedMarkIds.length
      ? selectedMarkIds
      : selectedMarkId != null
        ? [selectedMarkId]
        : []
    ).map((id) => String(id)),
  );
  const copiedMarks = (marks || [])
    .filter((m) => markIds.has(String(m.id)))
    .map((m) => clone(m));
  const pbIds = new Set((selectedPasteboardIds || []).map((id) => String(id)));
  const floats = (pasteboard || [])
    .filter((p) => pbIds.has(String(p.id)) && p?.assetId != null)
    .map((p) => clone(p));
  if (!photos.length && !copiedMarks.length && !floats.length) return null;
  return {
    fromPage,
    pasteCount: 0,
    slots: photos,
    marks: copiedMarks,
    pasteboard: floats,
  };
}

/**
 * Copia en la página destino.
 * Primera vez en otra página: misma posición.
 * Misma página o pegados repetidos: un pequeño desplazamiento.
 * `samePlace: true` fuerza posición original (pegar en todas las mesas).
 */
export function pasteMoodboardClipboard(
  page,
  clipboard,
  { pageIndex, samePlace = false } = {},
) {
  if (!clipboard) return null;
  const slotsIn = Array.isArray(clipboard.slots) ? clipboard.slots : [];
  const marksIn = Array.isArray(clipboard.marks) ? clipboard.marks : [];
  // Floats del pasteboard se pegan vía caller (addAssetsToPasteboard); aquí solo página.
  if (!slotsIn.length && !marksIn.length) return null;

  const samePage =
    !samePlace && Number(clipboard.fromPage) === Number(pageIndex);
  const step = samePlace
    ? 0
    : (samePage ? 1 : 0) + (Number(clipboard.pasteCount) || 0);
  const nudge = step * NUDGE;

  const baseSlots = Array.isArray(page?.slots) ? page.slots : [];
  let maxZ = baseSlots.reduce((max, s) => Math.max(max, Number(s.z) || 0), 0);
  const newSlotIds = [];
  const addedSlots = slotsIn.map((slot) => {
    const id = newSlotId("copy");
    newSlotIds.push(id);
    maxZ += 1;
    return clampSlot({
      ...slot,
      id,
      x: (Number(slot.x) || 0) + nudge,
      y: (Number(slot.y) || 0) + nudge,
      z: maxZ,
    });
  });

  const newMarkIds = [];
  const addedMarks = marksIn.map((mark) => {
    const id = newMarkId("copy");
    newMarkIds.push(id);
    // Quitar role de cabecera/pie + marcar free: si no, scrubChromeMarkDupes
    // borra la copia (1 marca por role / colapso de barras) → "pega y desaparece".
    const { role: _dropRole, ...markRest } = mark || {};
    const markFree = { ...markRest, free: 1 };
    if (markFree.type === "path" || markFree.type === "textpath") {
      return clampMark({
        ...markFree,
        id,
        points: (markFree.points || []).map((p) => ({
          x: (Number(p.x) || 0) + nudge,
          y: (Number(p.y) || 0) + nudge,
        })),
      });
    }
    if (markFree.type === "line" || markFree.type === "arrow") {
      return clampMark({
        ...markFree,
        id,
        x1: (Number(markFree.x1) || 0) + nudge,
        y1: (Number(markFree.y1) || 0) + nudge,
        x2: (Number(markFree.x2) || 0) + nudge,
        y2: (Number(markFree.y2) || 0) + nudge,
      });
    }
    return clampMark({
      ...markFree,
      id,
      x: (Number(markFree.x) || 0) + nudge,
      y: (Number(markFree.y) || 0) + nudge,
    });
  });

  return {
    slots: [...baseSlots.map((s) => clampSlot(s)), ...addedSlots],
    marks: [...(page?.marks || []).map((m) => clampMark(m)), ...addedMarks],
    newSlotIds,
    newMarkIds,
    nextClipboard: { ...clipboard, pasteCount: step + (samePage ? 0 : 1) },
  };
}

/**
 * Pega el mismo clipboard en todas las páginas (Ctrl+Shift+Alt+V).
 * Misma posición relativa en cada una (sin nudge entre páginas).
 */
export function pasteMoodboardClipboardOnAllPages(pages, clipboard) {
  const list = Array.isArray(pages) ? pages : [];
  if (!clipboard || !list.length) return null;
  const slotsIn = Array.isArray(clipboard.slots) ? clipboard.slots : [];
  const marksIn = Array.isArray(clipboard.marks) ? clipboard.marks : [];
  if (!slotsIn.length && !marksIn.length) return null;

  let any = false;
  const nextPages = list.map((page, pageIndex) => {
    const one = pasteMoodboardClipboard(
      page,
      { ...clipboard, pasteCount: 0 },
      { pageIndex, samePlace: true },
    );
    if (!one) return page;
    any = true;
    return {
      ...page,
      slots: one.slots,
      marks: one.marks,
    };
  });
  if (!any) return null;
  return {
    pages: nextPages,
    nextClipboard: {
      ...clipboard,
      pasteCount: (Number(clipboard.pasteCount) || 0) + 1,
    },
  };
}

/** Quita del pasteboard lo que se cortó (floats). */
export function pasteboardAfterMoodboardCut(pasteboard, clipboard) {
  if (!clipboard) return null;
  const cutIds = new Set((clipboard.pasteboard || []).map((p) => String(p.id)));
  if (!cutIds.size) return null;
  return (pasteboard || []).filter((p) => !cutIds.has(String(p.id)));
}

/** Quita de la página lo que se acaba de copiar al portapapeles (cortar). */
export function pageAfterMoodboardCut(page, clipboard) {
  if (!clipboard) return null;
  const cutSlotIds = new Set(
    (clipboard.slots || []).map((slot) => String(slot.id)),
  );
  const cutMarkIds = new Set(
    (clipboard.marks || []).map((mark) => String(mark.id)),
  );
  if (!cutSlotIds.size && !cutMarkIds.size) return null;

  let slots = (page?.slots || []).filter(
    (slot) => !cutSlotIds.has(String(slot.id)),
  );
  const marks = (page?.marks || []).filter(
    (mark) => !cutMarkIds.has(String(mark.id)),
  );
  return {
    slots: slots.map((slot) => clampSlot(slot)),
    marks: marks.map((mark) => clampMark(mark)),
  };
}
